/**
 * Hand-coded phone and digital communication situational phrases
 * (ESL → Japanese L1 kana).
 * License: CC BY-SA 4.0.
 *
 * J-L1-specific pain category — phone English is consistently cited as
 * a top anxiety point in Eikaiwa practitioner accounts (no visual cues,
 * fast speech, formulaic register learners haven\'t been drilled on).
 */

import type { SituationCard } from '../../situations/types.js';

const phoneComms: SituationCard[] = [
  {
    id: 'phone.opening-business',
    situationId: 'phone.opening-business',
    front: 'Hi, this is Alex from Acme — is now a good time to talk?',
    back: 'もしもし、Acme社のアレックスです。今お時間よろしいですか？',
    category: 'phone-comms',
    cefr: 'A2',
    translations: { ja: 'もしもし、アクメしゃのアレックスです。いまおじかんよろしいですか？' },
    speechAct: 'opening',
    formality: 'polite',
    channel: 'phone',
    pragmaticNote: {
      en: 'English phone opening: "this is X from Y" + "is now a good time?" The "is now a good time" check is standard politeness — skipping it can feel pushy.',
      ja: '英語の電話の出だしは「This is X from Y」のあとに「今いいですか？」と確認するのが定番。確認を省くと押し付けがましく感じられます。'
    }
  },
  {
    id: 'phone.cant-hear',
    situationId: 'phone.cant-hear',
    front: "Sorry, I'm losing you a bit — could you say that again?",
    back: 'すみません、少し音声が途切れていて……もう一度言っていただけますか？',
    category: 'phone-comms',
    cefr: 'B1',
    translations: { ja: 'すみません、すこしおんせいがとぎれていて……もういちどいっていただけますか？' },
    speechAct: 'repair',
    formality: 'polite',
    channel: 'phone',
    pragmaticNote: {
      en: '"I\'m losing you" is the polite English idiom for poor phone audio. More natural than "I can\'t hear you."',
      ja: '"I\'m losing you"は電波・音声が悪いときの定型句。"I can\'t hear you"よりも自然。'
    }
  },
  {
    id: 'phone.leave-message',
    situationId: 'phone.leave-message',
    front: "Could you take a message, please?",
    back: '伝言をお願いできますか？',
    category: 'phone-comms',
    cefr: 'A2',
    translations: { ja: 'でんごんをおねがいできますか？' },
    speechAct: 'request',
    formality: 'polite',
    channel: 'phone'
  },
  {
    id: 'phone.callback',
    situationId: 'phone.callback',
    front: "Could you have her call me back when she gets a chance?",
    back: '手が空いたときにかけ直してもらえると助かります。',
    category: 'phone-comms',
    cefr: 'B1',
    translations: { ja: 'てがあいたときにかけなおしてもらえるとたすかります。' },
    speechAct: 'request',
    formality: 'polite',
    channel: 'phone'
  },
  {
    id: 'phone.put-on-hold',
    situationId: 'phone.put-on-hold',
    front: "Could you hold for just a moment? I'll find that information for you.",
    back: '少々お待ちいただけますか？お調べします。',
    category: 'phone-comms',
    cefr: 'B1',
    translations: { ja: 'しょうしょうおまちいただけますか？おしらべします。' },
    speechAct: 'request',
    formality: 'polite',
    channel: 'phone'
  },
  {
    id: 'phone.closing',
    situationId: 'phone.closing',
    front: "Thanks for calling — talk to you soon.",
    back: 'お電話ありがとうございました。また連絡します。',
    category: 'phone-comms',
    cefr: 'A2',
    translations: { ja: 'おでんわありがとうございました。またれんらくします。' },
    speechAct: 'closing',
    formality: 'polite',
    channel: 'phone'
  },
  {
    id: 'phone.video-meeting-late',
    situationId: 'phone.video-meeting-late',
    front: "Sorry I'm late joining — my previous meeting ran over.",
    back: '遅れてすみません。前のミーティングが長引いてしまって。',
    category: 'phone-comms',
    cefr: 'B1',
    translations: { ja: 'おくれてすみません。まえのミーティングがながびいてしまって。' },
    speechAct: 'apology',
    formality: 'polite',
    channel: 'chat'
  },
  {
    id: 'phone.audio-trouble',
    situationId: 'phone.audio-trouble',
    front: "I think you might be on mute — we can\'t hear you.",
    back: 'マイクがミュートになっているかもしれません。声が聞こえません。',
    category: 'phone-comms',
    cefr: 'B1',
    translations: { ja: 'マイクがミュートになっているかもしれません。こえがきこえません。' },
    speechAct: 'repair',
    formality: 'casual',
    channel: 'chat'
  },
  {
    id: 'phone.email-followup',
    situationId: 'phone.email-followup',
    front: "I'll send a follow-up email summarizing what we discussed.",
    back: '今お話しした内容をまとめてメールでお送りします。',
    category: 'phone-comms',
    cefr: 'B2',
    translations: { ja: 'いまおはなししたないようをまとめてメールでおおくりします。' },
    speechAct: 'offering',
    formality: 'polite',
    channel: 'phone'
  },
  {
    id: 'phone.text-cant-talk',
    situationId: 'phone.text-cant-talk',
    front: "Can\'t talk now — in a meeting. Will text you in an hour.",
    back: '今会議中で話せない。1時間後に連絡します。',
    category: 'phone-comms',
    cefr: 'C1',
    translations: { ja: 'いまかいぎちゅうではなせない。１じかんごにれんらくします。' },
    speechAct: 'refusal',
    formality: 'casual',
    channel: 'chat',
    notes: {
      en: 'C1 micro-register: telegraphic chat/SMS style. Drops subjects, uses sentence fragments. Native speakers do this constantly; learners often over-write.',
      ja: 'チャットやSMSの省略文体。主語を省き、文を短く切るのが自然。学習者はつい完全な文を書きがちです。'
    }
  }
];

export default phoneComms;
